'use strict';
import { useRef, useEffect, useLayoutEffect, useCallback } from 'react';

export const Corner = {
  TOP_LEFT: 'topLeft',
  TOP_RIGHT: 'topRight',
  BOTTOM_LEFT: 'bottomLeft',
  BOTTOM_RIGHT: 'bottomRight',
};

const INITIAL_CORNER = Corner.BOTTOM_RIGHT;
const SNAP_TRANSITION = 'transform 0.28s cubic-bezier(0.4, 0, 0.2, 1)';
const FLOATING_WINDOW_STYLE = {
  top: 0,
  left: 0,
  transform: 'translate(0px, 0px)',
  transition: SNAP_TRANSITION,
  touchAction: 'none',
  // Inline style gets no auto-prefix; Safari 15 needs -webkit-user-select.
  WebkitUserSelect: 'none',
  userSelect: 'none',
  cursor: 'grab',
};

const getViewportBounds = () => {
  if (typeof window === 'undefined') return { width: 0, height: 0 };
  const viewport = window.visualViewport;
  return {
    width: viewport?.width || window.innerWidth,
    height: viewport?.height || window.innerHeight,
  };
};

const getContainerBounds = element => {
  const viewport = getViewportBounds();
  const parent = element?.offsetParent;
  if (parent && parent.clientWidth) {
    return {
      width: parent.clientWidth,
      // Keep the window visible when the iOS URL bar changes the viewport height.
      height: Math.min(
        parent.clientHeight,
        viewport.height || parent.clientHeight
      ),
    };
  }
  return viewport;
};

const getElementSize = element => ({
  width: element.offsetWidth,
  height: element.offsetHeight,
});

const applyPosition = (element, position) => {
  element.style.transform = `translate(${position.x}px, ${position.y}px)`;
};

export const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export const getCornerPosition = ({ corner, size, bounds, margins }) => {
  const isLeft = corner === Corner.TOP_LEFT || corner === Corner.BOTTOM_LEFT;
  const isTop = corner === Corner.TOP_LEFT || corner === Corner.TOP_RIGHT;
  return {
    x: isLeft
      ? margins.horizontal
      : Math.max(
          margins.horizontal,
          bounds.width - margins.horizontal - size.width
        ),
    y: isTop
      ? margins.top
      : Math.max(margins.top, bounds.height - margins.bottom - size.height),
  };
};

export const getNearestCorner = ({ position, size, bounds }) => {
  const isLeft = position.x + size.width / 2 < bounds.width / 2;
  const isTop = position.y + size.height / 2 < bounds.height / 2;
  if (isTop) return isLeft ? Corner.TOP_LEFT : Corner.TOP_RIGHT;
  return isLeft ? Corner.BOTTOM_LEFT : Corner.BOTTOM_RIGHT;
};

/**
 * Make a floating window draggable and snap it to a corner.
 * The caller controls its size with CSS; dragging stays within its offsetParent.
 * @param {object} params
 * @param {boolean} [params.isEnabled] - Whether dragging is enabled.
 * @param {object} params.margins - { horizontal, top, bottom } px from container edges.
 * @returns {{ ref: object, style: object }} Element ref and positioning style.
 * @example
 * const floatingWindow = useDraggableFloatingWindow({ isEnabled: true, margins });
 * return <StyledSizedDiv ref={floatingWindow.ref} style={floatingWindow.style} />;
 */
const useDraggableFloatingWindow = ({ isEnabled = false, margins }) => {
  const marginsRef = useRef(margins);
  marginsRef.current = margins;

  const ref = useRef(null);
  const cornerRef = useRef(INITIAL_CORNER);
  const positionRef = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);

  const snapToCorner = useCallback(corner => {
    const element = ref.current;
    if (!element) return;
    cornerRef.current = corner;
    const position = getCornerPosition({
      corner,
      size: getElementSize(element),
      bounds: getContainerBounds(element),
      margins: marginsRef.current,
    });
    positionRef.current = position;
    element.style.transition = SNAP_TRANSITION;
    applyPosition(element, position);
  }, []);

  const reanchorToCorner = useCallback(() => {
    if (isDraggingRef.current) return;
    snapToCorner(cornerRef.current);
  }, [snapToCorner]);

  // Re-entering the floating window always starts from the initial corner.
  useEffect(() => {
    if (!isEnabled) {
      cornerRef.current = INITIAL_CORNER;
    }
  }, [isEnabled]);

  // Reposition when the element, container, viewport, or margins change.
  const { horizontal, top, bottom } = margins;
  useLayoutEffect(() => {
    if (!isEnabled) return;
    const element = ref.current;
    if (!element) return;

    reanchorToCorner();

    const container = element.offsetParent;
    const observer = new ResizeObserver(() => reanchorToCorner());
    observer.observe(element);
    if (container) observer.observe(container);
    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', reanchorToCorner);
    return () => {
      observer.disconnect();
      viewport?.removeEventListener('resize', reanchorToCorner);
    };
  }, [isEnabled, horizontal, top, bottom, reanchorToCorner]);

  // Update transform directly so dragging does not re-render camera content.
  useEffect(() => {
    const element = ref.current;
    if (!isEnabled || !element) return;

    // Snapshot geometry once to avoid layout reads on every pointer move.
    let dragStart = null;
    let frameId = null;

    const handlePointerDown = event => {
      if (dragStart || event.target.closest?.('[data-no-drag]')) {
        return;
      }
      element.setPointerCapture?.(event.pointerId);
      isDraggingRef.current = true;
      element.style.transition = 'none';
      dragStart = {
        pointerId: event.pointerId,
        pointerX: event.clientX,
        pointerY: event.clientY,
        positionX: positionRef.current.x,
        positionY: positionRef.current.y,
        size: getElementSize(element),
        bounds: getContainerBounds(element),
      };
    };

    const handlePointerMove = event => {
      if (!dragStart || event.pointerId !== dragStart.pointerId) return;
      const { bounds, size } = dragStart;
      positionRef.current = {
        x: clamp(
          dragStart.positionX + (event.clientX - dragStart.pointerX),
          0,
          Math.max(0, bounds.width - size.width)
        ),
        y: clamp(
          dragStart.positionY + (event.clientY - dragStart.pointerY),
          0,
          Math.max(0, bounds.height - size.height)
        ),
      };
      if (frameId === null) {
        frameId = requestAnimationFrame(() => {
          frameId = null;
          applyPosition(element, positionRef.current);
        });
      }
    };

    const handlePointerEnd = event => {
      if (!dragStart || event.pointerId !== dragStart.pointerId) return;
      const { size } = dragStart;
      dragStart = null;
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
        frameId = null;
      }
      isDraggingRef.current = false;
      snapToCorner(
        getNearestCorner({
          position: positionRef.current,
          size,
          bounds: getContainerBounds(element),
        })
      );
    };

    element.addEventListener('pointerdown', handlePointerDown);
    element.addEventListener('pointermove', handlePointerMove);
    element.addEventListener('pointerup', handlePointerEnd);
    element.addEventListener('pointercancel', handlePointerEnd);

    return () => {
      element.removeEventListener('pointerdown', handlePointerDown);
      element.removeEventListener('pointermove', handlePointerMove);
      element.removeEventListener('pointerup', handlePointerEnd);
      element.removeEventListener('pointercancel', handlePointerEnd);
      if (frameId !== null) cancelAnimationFrame(frameId);
      isDraggingRef.current = false;
    };
  }, [isEnabled, snapToCorner]);

  return { ref, style: FLOATING_WINDOW_STYLE };
};

export default useDraggableFloatingWindow;
